/**
 * ShapeEditor - Full Lit + Shoelace based editor for authoring light-code grid
 * compositions.
 *
 * The editor's source of truth is the authored `GridComposition` (boxes that
 * carry a light code expanding into a flex grid; per-axis width/height locks).
 * It is expanded via `expandGrid` into the internal `ShapeComposition` only for
 * rendering and selection - the editor never hand-builds the nested tree.
 *
 * Design: documentation/engineering/configurator-light-code-grid.md.
 *
 * @example
 * ```html
 * <ptc-editor
 *   .grid=${myGrid}
 *   .assetManifest=${manifest}
 *   @grid-change=${handleChange}
 * ></ptc-editor>
 * ```
 */

import shapeeditorStyles from './ShapeEditor.styles.css?inline';
import { LitElement, html, PropertyValues, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type {
  ComputedOpening,
  AssetManifest,
  AssetCategory,
  AssetEntry,
  OpeningStyle,
  CompositionDefaults,
  RenderOptions,
} from '../types';
import {
  ShapeRenderer,
  LABEL_FONT_FAMILY,
  layoutLabelColumn,
  parseViewBox,
} from '../renderer/ShapeRenderer';
import {
  AssetManager,
  groupByFamily,
  offeredAssets,
  type AssetFamily,
} from '../models/AssetManager';
import { UNKNOWN_ASSET_SVG } from '../models/UnknownAsset';
import {
  expandGrid,
  createEmptyGrid,
  FRAME_THICKNESS_MM,
  type GridBox,
  type GridComposition,
  type GridConstraintIssue,
  type GridExpansionResult,
} from '../models/GridModel';

// Import Shoelace components
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import './ShapeThumbnail.js';

/**
 * Event detail for grid changes. Carries the authored grid plus any
 * over-constraint issues from the latest expansion (warn-and-render).
 */
export interface GridChangeDetail {
  grid: GridComposition;
  issues: GridConstraintIssue[];
}

/** Default opening style applied to new compositions (schematic look). */
const DEFAULT_OPENING_STYLE: OpeningStyle = {
  strokeColor: '#374151',
  strokeWidth: 2,
  fillColor: '#dbeafe',
  fillOpacity: 0.4,
};

/**
 * Canvas margin (px) around the frame in the preview. The dimension overlay
 * draws its outer tiers in this margin, so it widens when dimensions are on.
 * fitToExtents reserves the same px so the whole canvas (content + tiers) fits.
 */
const PREVIEW_PADDING_PX = 10;
// Must match ShapeThumbnail's dimensioned padding: the editor and the report
// card render the same drawing, so they have to reserve the same margin or the
// outermost dimension text is clipped in one of them and not the other.
const PREVIEW_PADDING_WITH_DIMS_PX = 80;

/** Composition defaults for a freshly created grid. */
const NEW_COMPOSITION_DEFAULTS: CompositionDefaults = {
  openingStyle: DEFAULT_OPENING_STYLE,
};

/** A path segment addressing a grid cell, e.g. `c2r1`. */
const CELL_SEGMENT = /^c(\d+)r(\d+)$/;

/** Map a constraint issue code to an sl-alert variant. */
function issueVariant(code: GridConstraintIssue['code']): 'warning' | 'danger' {
  return code === 'height-lock-void' || code === 'height-lock-overflow'
    ? 'danger'
    : 'warning';
}

/** Map a constraint issue code to an sl-icon name. */
function issueIcon(code: GridConstraintIssue['code']): string {
  return code === 'height-lock-void' || code === 'height-lock-overflow'
    ? 'x-circle'
    : 'exclamation-triangle';
}

/**
 * Full-featured editor component for light-code grid compositions.
 *
 * @fires grid-change - When the authored grid is modified
 * @fires opening-select - When a box is selected
 */
/**
 * One type's section of the picker as it is drawn: the type, and its families
 * with each family's full membership (`assets`, which decides heading or row) and
 * the members the search leaves on screen (`shown`).
 */
interface PickerCategory {
  key: string;
  category: AssetCategory;
  families: Array<AssetFamily & { shown: AssetEntry[] }>;
}

@customElement('ptc-editor')
export class ShapeEditor extends LitElement {
  static styles = unsafeCSS(shapeeditorStyles);

  /** The authored grid - the editor's source of truth. */
  @property({ type: Object })
  grid: GridComposition | null = null;

  @property({ type: Object })
  assetManifest: AssetManifest | null = null;

  @property({ type: Object, attribute: false })
  assetManager: AssetManager | null = null;

  /**
   * The thumbnails (picker tiles and rows, the Selected Light row) whose glyph file
   * failed to load, per manager, keyed
   * `category/id`. Keyed by manager so a replaced manager (a new manifest) tries again,
   * and weakly so a discarded one is not kept alive by this record.
   */
  private failedTileLoads = new WeakMap<AssetManager, Set<string>>();

  @property({ type: Number })
  previewScale = 0.5;

  /**
   * Draw the opening / handing labels declared by each applied asset. On by
   * default so the editor preview matches the drawing that gets saved.
   */
  @property({ type: Boolean, attribute: 'show-labels' })
  showLabels = true;

  /** Show the dimension overlay (overall + per-track sizes, lock indicators). */
  @property({ type: Boolean, attribute: 'show-dimensions' })
  showDimensions = true;

  @property({ type: Boolean })
  readonly = false;

  /**
   * The size a new composition starts with, in mm - the host's frame dimensions, so a
   * drawing begins as the frame it depicts rather than at 1000 x 1000 (customer feedback,
   * "should the new composition start with the frame height and width as previously
   * entered?"; architect 2026-09-06: yes, prefilled, not locked). Null keeps the editor's
   * own default. Read when the New Composition dialog opens, so a host that changes its
   * dimensions while the editor is mounted is honoured too.
   */
  @property({ type: Object, attribute: false })
  defaultSize: { width: number; height: number } | null = null;

  /**
   * Path of the primary selected box: `root` or a cell path like `root/c2r1`.
   * Drives the single-box properties panel and the asset panel.
   */
  @state()
  private selectedPath: string | null = null;

  /**
   * All currently selected box paths (always includes {@link selectedPath}).
   * More than one => the batch-sizing panel; shift/ctrl-click extends it. Kept
   * as an immutable Set (reassigned on change) so Lit re-renders.
   */
  @state()
  private selectedPaths = new Set<string>();

  @state()
  private svgContent: string | null = null;

  @state()
  private computedRoot: ComputedOpening | null = null;

  @state()
  private loading = false;

  @state()
  private assetSearchQuery = '';

  /** Over-constraint issues from the latest grid expansion (warn-and-render). */
  @state()
  private issues: GridConstraintIssue[] = [];

  // New composition dialog state
  @state() private newDialogOpen = false;
  @state() private newDialogWidth = 1000;
  @state() private newDialogHeight = 1000;
  @state() private newDialogLightcode = '1';

  private renderer: ShapeRenderer | null = null;

  /**
   * True when the next `grid` change came from an internal edit (via
   * {@link applyGrid}), so {@link updated} should NOT auto-fit. An external
   * load (host sets `.grid`) leaves it false, so opening a model fits at once.
   */
  private internalGridChange = false;

  /** Derived expansion of {@link grid}; recomputed on every grid change. */
  private expansion: GridExpansionResult | null = null;

  /** Undo history stack - stores JSON snapshots of the authored grid. */
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private maxHistorySize = 50;

  connectedCallback(): void {
    super.connectedCallback();
    this.renderer = new ShapeRenderer();

    // Listen for keyboard shortcuts
    this.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux) for undo
    if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      this.undo();
    }
    // Cmd+Shift+Z or Ctrl+Y for redo
    if (
      ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') ||
      ((e.metaKey || e.ctrlKey) && e.key === 'y')
    ) {
      e.preventDefault();
      this.redo();
    }
  };

  // ==========================================================================
  // Grid state: history, mutation, expansion
  // ==========================================================================

  /** Push the current grid to the undo stack before making changes. */
  private pushToHistory(): void {
    if (!this.grid) return;
    this.undoStack.push(JSON.stringify(this.grid));
    this.redoStack = [];
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
  }

  /** Undo the last change. */
  undo(): void {
    if (this.undoStack.length === 0) return;
    if (this.grid) this.redoStack.push(JSON.stringify(this.grid));
    const restored = JSON.parse(this.undoStack.pop()!) as GridComposition;
    this.applyGrid(restored);
  }

  /** Redo the last undone change. */
  redo(): void {
    if (this.redoStack.length === 0) return;
    if (this.grid) this.undoStack.push(JSON.stringify(this.grid));
    const restored = JSON.parse(this.redoStack.pop()!) as GridComposition;
    this.applyGrid(restored);
  }

  /** Check if undo is available. */
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** Check if redo is available. */
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Commit a new authored grid: store it, recompute the expansion synchronously
   * (so the emitted issues and the next render are consistent), and emit.
   */
  private applyGrid(next: GridComposition): void {
    // Mark this as an internal edit so updated() skips the load auto-fit.
    this.internalGridChange = true;
    this.grid = next;
    this.recomputeExpansion();
    this.emitGridChange();
  }

  private emitGridChange(): void {
    if (!this.grid) return;
    this.dispatchEvent(
      new CustomEvent<GridChangeDetail>('grid-change', {
        detail: { grid: this.grid, issues: this.issues },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Expand the authored grid into the render composition + issues + labels. */
  private recomputeExpansion(): void {
    if (!this.grid) {
      this.expansion = null;
      this.issues = [];
      return;
    }
    this.expansion = expandGrid(this.grid);
    this.issues = this.expansion.issues;
  }

  private cloneGrid(): GridComposition {
    return JSON.parse(JSON.stringify(this.grid)) as GridComposition;
  }

  /** Mutate the whole grid (composition-level edits: name, size, defaults). */
  private updateGrid(mutator: (grid: GridComposition) => void): void {
    if (!this.grid) return;
    this.pushToHistory();
    const next = this.cloneGrid();
    mutator(next);
    this.applyGrid(next);
  }

  /**
   * Set the composition's overall size in mm, as one undoable edit - exactly what typing
   * both values into the Composition panel's Height and Width inputs does, in one step.
   *
   * Public so a host can offer "correct the dimensions" beside its own warning that the
   * drawing does not match the thing it describes (the AS 2047 report's frame), without
   * reaching into this panel's inputs. Goes through `updateGrid` so the edit lands in the
   * undo history and emits `grid-change` like any other. Returns false, and changes nothing,
   * when there is no composition or a value is not a positive number.
   */
  setCompositionSize(size: { width: number; height: number }): boolean {
    if (!this.grid) return false;
    if (!(size.width > 0) || !(size.height > 0)) return false;
    this.updateGrid((g) => {
      g.size.height = { value: size.height, unit: 'mm' };
      g.size.width = { value: size.width, unit: 'mm' };
    });
    this.refitAfterRender();
    return true;
  }

  // ==========================================================================
  // Path <-> box resolution
  // ==========================================================================

  /** Split a selection path into cell segments, or null if not a box path. */
  private static cellSegments(path: string): string[] | null {
    const parts = path.split('/');
    if (parts[0] !== 'root') return null;
    const segs = parts.slice(1);
    for (const s of segs) {
      if (!CELL_SEGMENT.test(s)) return null;
    }
    return segs;
  }

  /** Read the authored box at a path; an unmaterialised cell reads as empty. */
  private resolveBox(path: string): GridBox | null {
    if (!this.grid) return null;
    const segs = ShapeEditor.cellSegments(path);
    if (segs === null) return null;
    let box: GridBox = this.grid.root;
    for (const seg of segs) {
      const child = box.overrides?.[seg];
      if (!child) return {};
      box = child;
    }
    return box;
  }

  /** Resolve (materialising sparse overrides) the box at a path for mutation. */
  private boxAtPath(grid: GridComposition, path: string): GridBox {
    const segs = ShapeEditor.cellSegments(path) ?? [];
    let box = grid.root;
    for (const seg of segs) {
      box.overrides = box.overrides ?? {};
      box.overrides[seg] = box.overrides[seg] ?? {};
      box = box.overrides[seg];
    }
    return box;
  }

  /** Mutate a single authored box, then commit + emit. */
  private mutateBox(path: string, mutator: (box: GridBox) => void): void {
    if (!this.grid) return;
    this.pushToHistory();
    const next = this.cloneGrid();
    mutator(this.boxAtPath(next, path));
    this.applyGrid(next);
  }

  /**
   * Cell keys (`c{col}r{row}`) a light code produces, in column-major top->bottom
   * order. Mirrors `GridModel.parseLightCode` (clamp 1-9, skip non-digits).
   */
  private cellKeys(code: string | undefined): string[] {
    const keys: string[] = [];
    let col = 0;
    for (const ch of code ?? '') {
      if (ch < '0' || ch > '9') continue;
      col++;
      const rows = Math.min(Math.max(parseInt(ch, 10), 1), 9);
      for (let r = 1; r <= rows; r++) keys.push(`c${col}r${r}`);
    }
    return keys;
  }

  private isSubdivided(box: GridBox): boolean {
    return this.cellKeys(box.lightCode).length > 1;
  }

  // ==========================================================================
  // Box edits: light code, locks, assets
  // ==========================================================================

  private setBoxLightCode(path: string, raw: string): void {
    const code = raw.replace(/[^0-9]/g, '');
    this.mutateBox(path, (box) => {
      if (this.cellKeys(code).length <= 1) {
        // Single light (or empty) -> terminal box; cells no longer exist.
        delete box.lightCode;
        delete box.overrides;
      } else {
        box.lightCode = code;
        this.pruneOverrides(box);
      }
    });
  }

  /** Drop override entries whose cell no longer exists under the light code. */
  private pruneOverrides(box: GridBox): void {
    if (!box.overrides) return;
    const valid = new Set(this.cellKeys(box.lightCode));
    for (const key of Object.keys(box.overrides)) {
      if (!valid.has(key)) delete box.overrides[key];
    }
    if (Object.keys(box.overrides).length === 0) delete box.overrides;
  }

  private setBoxLock(
    path: string,
    axis: 'width' | 'height',
    value: number | null
  ): void {
    this.mutateBox(path, (box) => {
      if (axis === 'width') {
        if (value && value > 0) box.lockedWidth = { value, unit: 'mm' };
        else delete box.lockedWidth;
      } else {
        if (value && value > 0) box.lockedHeight = { value, unit: 'mm' };
        else delete box.lockedHeight;
      }
    });
  }

  /** Current rendered size (mm) of the box at a path, for a sensible lock default. */
  private computedMm(path: string, axis: 'width' | 'height'): number {
    const node = this.findComputedOpeningById(path);
    if (!node) {
      const size = this.grid?.size;
      return axis === 'width'
        ? (size?.width.value ?? 1000)
        : (size?.height.value ?? 1000);
    }
    const px = axis === 'width' ? node.bounds.width : node.bounds.height;
    return Math.max(1, Math.round(px / this.previewScale));
  }

  private handleAssetSelect(category: string, assetName: string): void {
    if (!this.selectedPath) return;

    const categories = this.assetManifest?.categories ?? {};
    const selectedCategory = categories[category] as AssetCategory | undefined;
    const isOverlay = selectedCategory?.overlay ?? false;

    // A base (non-overlay) asset is the opening's single operation glyph, so
    // selecting one replaces whatever base asset was there. Overlay categories
    // stack freely; none ship today, but the manifest still supports them.
    const isSingleSelect = !isOverlay;

    this.mutateBox(this.selectedPath, (box) => {
      if (!box.assets) box.assets = [];

      if (category === '' && assetName === '') {
        delete box.assets;
        return;
      }

      const existingIndex = box.assets.findIndex(
        (a) => a.category === category && a.name === assetName
      );
      if (existingIndex >= 0) {
        // Toggle off.
        box.assets.splice(existingIndex, 1);
      } else {
        if (!isOverlay) {
          // Base operation: remove any existing base (non-overlay) assets first.
          box.assets = box.assets.filter((a) => {
            const cat = categories[a.category] as AssetCategory | undefined;
            return cat?.overlay === true;
          });
        } else if (isSingleSelect) {
          box.assets = box.assets.filter((a) => a.category !== category);
        }
        box.assets.push({ category, name: assetName });
      }
      if (box.assets.length === 0) delete box.assets;
    });
  }

  // ==========================================================================
  // New composition / export / fit
  // ==========================================================================

  private applyNewComposition(): void {
    const code = this.newDialogLightcode.replace(/[^0-9]/g, '');
    const width = this.newDialogWidth > 0 ? this.newDialogWidth : 1000;
    const height = this.newDialogHeight > 0 ? this.newDialogHeight : 1000;

    const grid = createEmptyGrid(
      'New Composition',
      width,
      height,
      NEW_COMPOSITION_DEFAULTS
    );
    if (this.cellKeys(code).length > 1) grid.root.lightCode = code;

    this.pushToHistory();
    this.selectedPath = 'root';
    this.selectedPaths = new Set(['root']);
    this.newDialogOpen = false;
    this.applyGrid(grid);

    // Zoom-to-extents once the new composition has laid out (issue #168 item 4).
    void this.updateComplete.then(() => this.fitToExtents());
  }

  /**
   * Re-run fit-to-extents once the next render settles. Used when a
   * composition-dimension edit (overall height/width) changes the extents so
   * the preview re-zooms to fit automatically, matching new-composition create.
   */
  private refitAfterRender(): void {
    void this.updateComplete.then(() => this.fitToExtents());
  }

  /**
   * Set previewScale so the whole composition fits the preview area. Measures
   * the live preview-area box; only meaningful for mm-sized compositions.
   */
  private fitToExtents(): void {
    const area = this.renderRoot.querySelector(
      '.preview-area'
    ) as HTMLElement | null;
    if (!area || !this.grid) return;
    const { width, height } = this.grid.size;
    if (width.unit !== 'mm' || height.unit !== 'mm') return;
    const margin = 32;
    // The rendered canvas is gridMM*scale + 2*renderPad (fixed px), and the
    // dimension tiers live in that pad. Reserve it so the whole canvas fits,
    // not just the frame - otherwise the dimensions overflow the preview.
    const renderPad = this.showDimensions
      ? PREVIEW_PADDING_WITH_DIMS_PX
      : PREVIEW_PADDING_PX;
    const availW = area.clientWidth - margin * 2 - renderPad * 2;
    const availH = area.clientHeight - margin * 2 - renderPad * 2;
    if (availW <= 0 || availH <= 0 || width.value <= 0 || height.value <= 0) {
      return;
    }
    const scale = Math.min(availW / width.value, availH / height.value);
    this.previewScale = Math.max(0.1, Math.min(4, scale));
  }

  /** Export the rendered composition as a standalone SVG file. */
  private async exportSvg(): Promise<void> {
    const composition = this.expansion?.composition;
    if (!composition || !this.renderer) return;

    try {
      const result = await this.renderer.render(composition);
      const blob = new Blob([result.svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${composition.name.replace(/\s+/g, '-').toLowerCase()}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export SVG:', e);
    }
  }

  // ==========================================================================
  // Asset manager + preview render + selection
  // ==========================================================================

  /** Initialize AssetManager from the assetManifest property. */
  private async initAssetManager(): Promise<void> {
    if (!this.assetManifest) return;

    const manager = new AssetManager('/svg/');
    await manager.loadManifest(this.assetManifest);
    this.assetManager = manager;
  }

  updated(changedProps: PropertyValues): void {
    // Auto-create AssetManager when assetManifest is set.
    if (changedProps.has('assetManifest') && this.assetManifest) {
      void this.initAssetManager();
    }

    // Re-expand when the authored grid is replaced from outside.
    if (changedProps.has('grid')) {
      this.recomputeExpansion();
      // Auto-fit on an external load (open/reopen a model), but not on internal
      // edits - those manage their own refit where the extents actually change.
      if (this.internalGridChange) {
        this.internalGridChange = false;
      } else if (this.grid) {
        this.refitAfterRender();
      }
    }

    // Toggling the dimensions overlay changes the canvas margin, so refit.
    if (changedProps.has('showDimensions')) {
      this.refitAfterRender();
    }

    if (
      changedProps.has('grid') ||
      changedProps.has('previewScale') ||
      changedProps.has('showLabels') ||
      changedProps.has('showDimensions') ||
      changedProps.has('assetManager')
    ) {
      void this.renderPreview();
    }

    if (changedProps.has('selectedPath') || changedProps.has('selectedPaths')) {
      this.updateSelectionHighlight();
    }
  }

  private updateSelectionHighlight(): void {
    this.shadowRoot
      ?.querySelectorAll('.preview-svg rect.selected')
      .forEach((el) => el.classList.remove('selected'));

    for (const path of this.selectedPaths) {
      this.shadowRoot
        ?.querySelector(`.preview-svg rect[data-opening-id="${path}"]`)
        ?.classList.add('selected');
    }
  }

  private async renderPreview(): Promise<void> {
    const composition = this.expansion?.composition ?? null;
    if (!composition || !this.renderer) {
      this.svgContent = null;
      this.computedRoot = null;
      return;
    }

    this.loading = true;

    try {
      if (this.assetManager) {
        this.renderer.setAssetManager(this.assetManager);
      }

      const options: RenderOptions = {
        scale: this.previewScale,
        showLabels: this.showLabels,
        showBoundaries: true,
        showDimensions: this.showDimensions,
        // Transparent so the sheet colour comes from CSS (.preview-container)
        // and can follow the theme. A baked-in white <rect> would sit inside
        // the SVG where no stylesheet can reach it. The EXPORT path is a
        // separate hidden <ptc-thumbnail> and is unaffected.
        backgroundColor: 'transparent',
        // Widen the canvas margin when dimensions are on so the outer overall
        // tiers have room outside the frame.
        padding: this.showDimensions
          ? PREVIEW_PADDING_WITH_DIMS_PX
          : PREVIEW_PADDING_PX,
      };

      const result = await this.renderer.render(composition, options);
      this.svgContent = result.svg;
      this.computedRoot = result.computedRoot;

      // Re-apply selection highlight after re-render.
      await this.updateComplete;
      this.updateSelectionHighlight();
    } catch (e) {
      console.error('Render failed:', e);
    } finally {
      this.loading = false;
    }
  }

  /** Find a computed opening by id (== grid path) in the computed tree. */
  private findComputedOpeningById(
    id: string,
    root?: ComputedOpening
  ): ComputedOpening | null {
    const computed = root ?? this.computedRoot;
    if (!computed) return null;
    if (computed.opening.id === id) return computed;
    if (computed.children) {
      for (const child of computed.children) {
        const found = this.findComputedOpeningById(id, child);
        if (found) return found;
      }
    }
    return null;
  }

  private handlePreviewClick(event: MouseEvent): void {
    if (this.readonly) return;

    const target = event.target as Element;
    const openingEl = target.closest('[data-opening-id]');
    if (!openingEl) return;

    const id = openingEl.getAttribute('data-opening-id');
    if (!id) return;
    // Only root + cell paths are selectable; structural column containers are not.
    if (id !== 'root' && ShapeEditor.cellSegments(id) === null) return;

    // Shift / Ctrl / Cmd extends the selection (batch sizing across columns);
    // a plain click selects just this box.
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      this.toggleSelect(id);
    } else {
      this.selectBox(id);
    }
  }

  private selectBox(path: string): void {
    this.selectedPath = path;
    this.selectedPaths = new Set([path]);
    this.dispatchEvent(
      new CustomEvent('opening-select', {
        detail: { openingId: path },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Toggle a box in the multi-selection, keeping a sensible primary. */
  private toggleSelect(path: string): void {
    const next = new Set(this.selectedPaths);
    if (next.has(path)) {
      next.delete(path);
      if (this.selectedPath === path) {
        this.selectedPath = next.size > 0 ? [...next][next.size - 1] : null;
      }
    } else {
      next.add(path);
      this.selectedPath = path;
    }
    this.selectedPaths = next;
  }

  /**
   * Lock (or clear) one axis across several boxes in a single history step -
   * the batch behind cross-column multi-select. Each path resolves within its
   * own parent track; over-constraint is surfaced by the next expansion.
   */
  private setBoxLockMany(
    paths: string[],
    axis: 'width' | 'height',
    value: number | null
  ): void {
    if (!this.grid || paths.length === 0) return;
    this.pushToHistory();
    const next = this.cloneGrid();
    for (const path of paths) {
      const box = this.boxAtPath(next, path);
      if (axis === 'width') {
        if (value && value > 0) box.lockedWidth = { value, unit: 'mm' };
        else delete box.lockedWidth;
      } else {
        if (value && value > 0) box.lockedHeight = { value, unit: 'mm' };
        else delete box.lockedHeight;
      }
    }
    this.applyGrid(next);
  }

  /**
   * If all currently selected paths share the same parent box and the same
   * column index (c-part of the last cell segment), return those details so the
   * batch panel can show an "Equalize heights" button. Returns null otherwise.
   */
  private getSameColumnSelection(): {
    paths: string[];
    colIndex: number;
    parentPath: string;
  } | null {
    const paths = [...this.selectedPaths];
    if (paths.length < 2) return null;

    let parentPath: string | null = null;
    let colIndex: number | null = null;

    for (const path of paths) {
      const lastSlash = path.lastIndexOf('/');
      if (lastSlash < 0) return null;
      const seg = path.slice(lastSlash + 1);
      const match = CELL_SEGMENT.exec(seg);
      if (!match) return null;
      const col = parseInt(match[1], 10);
      const pp = path.slice(0, lastSlash);

      if (colIndex === null) {
        colIndex = col;
        parentPath = pp;
      } else if (col !== colIndex || pp !== parentPath) {
        return null;
      }
    }

    return colIndex !== null && parentPath !== null
      ? { paths, colIndex, parentPath }
      : null;
  }

  /**
   * If all currently selected paths share the same parent box and the same
   * row index (r-part of the last cell segment), return those details so the
   * batch panel can show an "Equalize widths" button. Returns null otherwise.
   */
  private getSameRowPositionSelection(): {
    paths: string[];
    rowIndex: number;
    parentPath: string;
  } | null {
    const paths = [...this.selectedPaths];
    if (paths.length < 2) return null;

    let parentPath: string | null = null;
    let rowIndex: number | null = null;

    for (const path of paths) {
      const lastSlash = path.lastIndexOf('/');
      if (lastSlash < 0) return null;
      const seg = path.slice(lastSlash + 1);
      const match = CELL_SEGMENT.exec(seg);
      if (!match) return null;
      const row = parseInt(match[2], 10);
      const pp = path.slice(0, lastSlash);

      if (rowIndex === null) {
        rowIndex = row;
        parentPath = pp;
      } else if (row !== rowIndex || pp !== parentPath) {
        return null;
      }
    }

    return rowIndex !== null && parentPath !== null
      ? { paths, rowIndex, parentPath }
      : null;
  }

  /**
   * Lock all selected cells (which must share the same column) to an equal
   * height that divides the column's full available height evenly across all
   * rows in that column. Unselected auto-rows absorb the same equal portion.
   */
  private equalizeSelectedHeights(): void {
    const sameCol = this.getSameColumnSelection();
    if (!sameCol || !this.grid) return;

    // Divider weight is the fixed schematic constant shared with the renderer.
    const thicknessMM = FRAME_THICKNESS_MM;

    const parentBox = this.resolveBox(sameCol.parentPath);
    if (!parentBox?.lightCode) return;

    let totalRows = 0;
    let col = 0;
    for (const ch of parentBox.lightCode) {
      if (ch < '0' || ch > '9') continue;
      col++;
      if (col === sameCol.colIndex) {
        totalRows = Math.min(Math.max(parseInt(ch, 10), 1), 9);
        break;
      }
    }
    if (totalRows < 2) return;

    const availableHeightMM =
      sameCol.parentPath === 'root'
        ? this.grid.size.height.value
        : this.computedMm(sameCol.parentPath, 'height');

    const gapsMM = (totalRows - 1) * thicknessMM;
    const equalHeight =
      Math.round(((availableHeightMM - gapsMM) / totalRows) * 10) / 10;

    if (equalHeight < 50) return;
    this.setBoxLockMany(sameCol.paths, 'height', equalHeight);
  }

  /**
   * Lock all selected cells (which must share the same row position) to an
   * equal width that divides the composition's full available width evenly
   * across all columns in the parent box. Unselected auto-columns absorb the
   * same equal portion.
   */
  private equalizeSelectedWidths(): void {
    const sameRow = this.getSameRowPositionSelection();
    if (!sameRow || !this.grid) return;

    // Divider weight is the fixed schematic constant shared with the renderer.
    const thicknessMM = FRAME_THICKNESS_MM;

    const parentBox = this.resolveBox(sameRow.parentPath);
    if (!parentBox?.lightCode) return;

    let totalCols = 0;
    for (const ch of parentBox.lightCode) {
      if (ch >= '0' && ch <= '9') totalCols++;
    }
    if (totalCols < 2) return;

    const availableWidthMM =
      sameRow.parentPath === 'root'
        ? this.grid.size.width.value
        : this.computedMm(sameRow.parentPath, 'width');

    const gapsMM = (totalCols - 1) * thicknessMM;
    const equalWidth =
      Math.round(((availableWidthMM - gapsMM) / totalCols) * 10) / 10;

    if (equalWidth < 50) return;
    this.setBoxLockMany(sameRow.paths, 'width', equalWidth);
  }

  /** Containing-opening path (drop the last cell segment), or null at root. */
  private static parentPath(path: string): string | null {
    const cut = path.lastIndexOf('/');
    return cut <= 0 ? null : path.slice(0, cut);
  }

  /**
   * Transiently outline an opening in the preview (used while hovering the
   * "select containing opening" button so the user sees the target before
   * clicking). Pass null to clear.
   */
  private setHoverPreview(path: string | null): void {
    this.shadowRoot
      ?.querySelectorAll('.preview-svg rect.hover-preview')
      .forEach((el) => el.classList.remove('hover-preview'));
    if (path) {
      this.shadowRoot
        ?.querySelector(`.preview-svg rect[data-opening-id="${path}"]`)
        ?.classList.add('hover-preview');
    }
  }

  // ==========================================================================
  // Render
  // ==========================================================================

  private renderConflictIssues(): ReturnType<typeof html> | typeof nothing {
    if (this.issues.length === 0) return nothing;
    return html`
      <div class="conflict-issues">
        ${this.issues.map(
          (issue) => html`
            <sl-alert variant=${issueVariant(issue.code)} open>
              <sl-icon slot="icon" name=${issueIcon(issue.code)}></sl-icon>
              ${issue.message}
            </sl-alert>
          `
        )}
      </div>
    `;
  }

  /**
   * The Selected Light section, the first thing in Properties (D-152, rule 15). A
   * light carries one drawing, so this replaced the Applied Assets panel with its
   * count and Clear All: it shows the selected light's drawing as a row, with the
   * control that removes it, or "No drawing applied". It is shown only when exactly
   * one undivided light is selected; with nothing selected, a subdivided box, or
   * several boxes there is no one light whose drawing it could name.
   */
  private renderSelectedLight(): ReturnType<typeof html> | typeof nothing {
    if (!this.grid || !this.selectedPath || this.selectedPaths.size > 1) {
      return nothing;
    }
    const box = this.resolveBox(this.selectedPath);
    if (!box || this.isSubdivided(box)) return nothing;

    const applied = box.assets ?? [];
    return html`
      <div class="panel-section selected-light">
        <div class="panel-section-title">Selected Light</div>
        ${
          applied.length === 0
            ? html`<div class="no-drawing-applied">No drawing applied</div>`
            : applied.map((asset) =>
                this.renderAssetRow(asset.category, asset.name, {
                  showType: true,
                  removable: true,
                })
              )
        }
      </div>
      <sl-divider></sl-divider>
    `;
  }

  private renderPropertiesPanel(): ReturnType<typeof html> {
    if (!this.grid) {
      return html`
        <div class="empty-state">
          <sl-icon name="window"></sl-icon>
          <p>Create a composition to begin</p>
        </div>
      `;
    }

    const box = this.selectedPath ? this.resolveBox(this.selectedPath) : null;

    return html`
      ${this.renderSelectedLight()} ${this.renderConflictIssues()}
      <div class="panel-section composition-size">
        <div class="panel-section-title">Composition</div>
        <div class="form-row">
          <label>Height (mm)</label>
          <sl-input
            type="number"
            size="small"
            min="100"
            max="10000"
            value=${this.grid.size.height.value}
            @sl-change=${(e: Event) => {
              const v = parseFloat((e.target as HTMLInputElement).value);
              if (!isNaN(v) && v > 0) {
                this.updateGrid((g) => {
                  g.size.height = { value: v, unit: 'mm' };
                });
                this.refitAfterRender();
              }
            }}
          ></sl-input>
        </div>
        <div class="form-row">
          <label>Width (mm)</label>
          <sl-input
            type="number"
            size="small"
            min="100"
            max="10000"
            value=${this.grid.size.width.value}
            @sl-change=${(e: Event) => {
              const v = parseFloat((e.target as HTMLInputElement).value);
              if (!isNaN(v) && v > 0) {
                this.updateGrid((g) => {
                  g.size.width = { value: v, unit: 'mm' };
                });
                this.refitAfterRender();
              }
            }}
          ></sl-input>
        </div>
      </div>

      <sl-divider></sl-divider>

      ${
        this.selectedPaths.size > 1
          ? this.renderBatchSizing()
          : box && this.selectedPath
            ? this.renderBoxSection(this.selectedPath, box)
            : html`
                <div class="empty-state">
                  <sl-icon name="cursor-fill"></sl-icon>
                  <p>Select a box to edit</p>
                </div>
              `
      }
    `;
  }

  private renderBoxSection(
    path: string,
    box: GridBox
  ): ReturnType<typeof html> {
    const lockedWidth = box.lockedWidth?.value ?? null;
    const lockedHeight = box.lockedHeight?.value ?? null;

    const parentPath = ShapeEditor.parentPath(path);

    return html`
      <div class="panel-section">
        <div class="panel-section-title-row">
          <div class="panel-section-title">Selected opening</div>
          ${
            parentPath
              ? html`
                  <sl-tooltip content="Select containing opening">
                    <sl-icon-button
                      name="box-arrow-up"
                      label="Select containing opening"
                      @click=${() => {
                        this.setHoverPreview(null);
                        this.selectBox(parentPath);
                      }}
                      @mouseenter=${() => this.setHoverPreview(parentPath)}
                      @mouseleave=${() => this.setHoverPreview(null)}
                      @focus=${() => this.setHoverPreview(parentPath)}
                      @blur=${() => this.setHoverPreview(null)}
                    ></sl-icon-button>
                  </sl-tooltip>
                `
              : nothing
          }
        </div>
        <div class="form-row">
          <label>Light Code</label>
          <sl-input
            size="small"
            inputmode="numeric"
            .value=${box.lightCode ?? ''}
            placeholder="blank = single light, e.g. 232"
            @sl-change=${(e: Event) =>
              this.setBoxLightCode(path, (e.target as HTMLInputElement).value)}
          ></sl-input>
          <div class="light-code-help">
            Each digit is a column; its value is the number of rows in that
            column. Blank is a single light.
          </div>
        </div>
      </div>

      <sl-divider></sl-divider>

      <div class="panel-section">
        <div class="panel-section-title">Sizing</div>
        ${this.renderSizeControl(path, 'height', lockedHeight)}
        ${this.renderSizeControl(path, 'width', lockedWidth)}
      </div>
    `;
  }

  /**
   * One Auto/fixed sizing control per axis. In this model a size only exists
   * as a lock (auto tracks store nothing and equalize), so there is no
   * separate toggle: typing a value locks the track, the reset returns it to
   * auto. The placeholder shows the current auto-equalized size so the user
   * can see (and match) it before pinning.
   */
  private renderSizeControl(
    path: string,
    axis: 'width' | 'height',
    locked: number | null
  ): ReturnType<typeof html> {
    const label = axis === 'width' ? 'Width' : 'Height';
    const isLocked = locked !== null;
    return html`
      <div class="form-row">
        <label>${label}</label>
        <div class="size-control">
          <sl-input
            class=${isLocked ? 'is-locked' : 'is-auto'}
            type="number"
            size="small"
            min="50"
            placeholder=${`Auto · ${this.computedMm(path, axis)} mm`}
            .value=${isLocked ? String(locked) : ''}
            @sl-change=${(e: Event) => {
              const v = parseFloat((e.target as HTMLInputElement).value);
              this.setBoxLock(path, axis, !isNaN(v) && v > 0 ? v : null);
            }}
          >
            <sl-icon
              slot="prefix"
              name=${isLocked ? 'lock-fill' : 'unlock'}
            ></sl-icon>
            <span slot="suffix">mm</span>
          </sl-input>
          <sl-tooltip content="Reset to Auto (equalised)">
            <sl-icon-button
              name="arrow-counterclockwise"
              label="Reset to Auto"
              ?disabled=${!isLocked}
              @click=${() => this.setBoxLock(path, axis, null)}
            ></sl-icon-button>
          </sl-tooltip>
        </div>
      </div>
    `;
  }

  /**
   * Batch-sizing panel shown when more than one opening is selected. Setting a
   * height or width locks that track on every selected opening at once (each
   * within its own parent); the reset clears the lock back to auto. Mixed
   * existing values aren't pre-filled - the field is an explicit "set all".
   *
   * When all selected cells share the same column, an "Equalize heights" button
   * appears that distributes the column height evenly across all rows. Similarly,
   * when all share the same row position, "Equalize widths" appears.
   */
  private renderBatchSizing(): ReturnType<typeof html> {
    const paths = [...this.selectedPaths];
    const sameCol = this.getSameColumnSelection();
    const sameRow = this.getSameRowPositionSelection();

    return html`
      <div class="panel-section">
        <div class="panel-section-title-row">
          <div class="panel-section-title">
            ${paths.length} openings selected
          </div>
          <sl-tooltip content="Clear selection">
            <sl-icon-button
              name="x-lg"
              label="Clear selection"
              @click=${() => this.clearSelection()}
            ></sl-icon-button>
          </sl-tooltip>
        </div>
        <div class="light-code-help">
          Set a size to lock the selected openings' tracks together, or reset to
          return them to auto.
        </div>
        ${this.renderBatchSizeRow(paths, 'height')}
        ${this.renderBatchSizeRow(paths, 'width')}
        ${
          sameCol
            ? html`
                <div class="form-row">
                  <sl-tooltip
                    content="Divide the column height evenly across all rows in this column and lock each row to that height."
                  >
                    <sl-button
                      size="small"
                      @click=${() => this.equalizeSelectedHeights()}
                    >
                      <sl-icon
                        slot="prefix"
                        name="distribute-vertical"
                      ></sl-icon>
                      Equalize heights
                    </sl-button>
                  </sl-tooltip>
                </div>
              `
            : nothing
        }
        ${
          sameRow
            ? html`
                <div class="form-row">
                  <sl-tooltip
                    content="Divide the composition width evenly across all columns and lock each column to that width."
                  >
                    <sl-button
                      size="small"
                      @click=${() => this.equalizeSelectedWidths()}
                    >
                      <sl-icon
                        slot="prefix"
                        name="distribute-horizontal"
                      ></sl-icon>
                      Equalize widths
                    </sl-button>
                  </sl-tooltip>
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  private renderBatchSizeRow(
    paths: string[],
    axis: 'width' | 'height'
  ): ReturnType<typeof html> {
    const label = axis === 'width' ? 'Width' : 'Height';
    return html`
      <div class="form-row">
        <label>${label}</label>
        <div class="size-control">
          <sl-input
            type="number"
            size="small"
            min="50"
            placeholder=${`Set ${label.toLowerCase()} on all`}
            @sl-change=${(e: Event) => {
              const v = parseFloat((e.target as HTMLInputElement).value);
              this.setBoxLockMany(paths, axis, !isNaN(v) && v > 0 ? v : null);
            }}
          >
            <span slot="suffix">mm</span>
          </sl-input>
          <sl-tooltip content="Reset all to Auto (equalised)">
            <sl-icon-button
              name="arrow-counterclockwise"
              label="Reset all to Auto"
              @click=${() => this.setBoxLockMany(paths, axis, null)}
            ></sl-icon-button>
          </sl-tooltip>
        </div>
      </div>
    `;
  }

  private renderAssetsPanel(): ReturnType<typeof html> {
    const box = this.selectedPath ? this.resolveBox(this.selectedPath) : null;

    if (!box) {
      return html`
        <div class="empty-state" style="padding: 2rem;">
          <sl-icon name="cursor-fill"></sl-icon>
          <p>Select a box<br />to manage assets</p>
        </div>
      `;
    }

    if (this.isSubdivided(box)) {
      return html`
        <div class="empty-state" style="padding: 2rem;">
          <sl-icon name="diagram-3"></sl-icon>
          <p>
            This box is subdivided.<br />Select a single light to add assets.
          </p>
        </div>
      `;
    }

    // The picker takes the whole panel: what the light carries shows in Properties
    // as Selected Light (D-152, rule 15), not in a panel above the picker.
    return this.renderAssetBrowser(box.assets ?? []);
  }

  /**
   * One drawing as a row: its thumbnail beside its full name. The ONE renderer for
   * both places a drawing reads by its full name (D-152, rules 14 and 15): a family
   * of one in the picker, where the row is pickable (it carries `asset-grid-item`
   * and applies on click, as a code tile does), and the Selected Light section,
   * where it names the type beneath and carries the control that removes it. The
   * title is the full name, the hook journeys find a drawing by. An id the manifest
   * cannot find names itself by id, and its thumbnail draws the question mark
   * (rule 12).
   */
  private renderAssetRow(
    categoryKey: string,
    assetId: string,
    options: {
      pick?: { selected: boolean };
      showType?: boolean;
      removable?: boolean;
    }
  ): ReturnType<typeof html> {
    const category: AssetCategory | undefined =
      this.assetManifest?.categories?.[categoryKey];
    const entry = category?.assets?.find((a) => a.id === assetId);
    const name = entry?.name ?? assetId;
    const classes = [
      'asset-row',
      options.pick ? 'asset-grid-item' : '',
      options.pick?.selected ? 'selected' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div
        class=${classes}
        title=${name}
        @click=${
          options.pick
            ? () => this.handleAssetSelect(categoryKey, assetId)
            : nothing
        }
      >
        <div class="asset-thumbnail">
          ${this.renderAssetThumbnail(categoryKey, assetId)}
        </div>
        <div class="asset-info">
          <div class="asset-name">
            ${name}
            ${
              category?.overlay
                ? html`<sl-badge variant="primary" class="overlay-badge"
                    >overlay</sl-badge
                  >`
                : nothing
            }
          </div>
          ${
            options.showType
              ? html`<div class="asset-category">
                  ${category?.name ?? categoryKey}
                </div>`
              : nothing
          }
        </div>
        ${
          options.removable
            ? html`<sl-tooltip content="Remove asset">
                <sl-icon-button
                  name="x-lg"
                  label="Remove asset"
                  @click=${() => this.handleAssetSelect(categoryKey, assetId)}
                ></sl-icon-button>
              </sl-tooltip>`
            : nothing
        }
      </div>
    `;
  }

  private renderAssetThumbnail(
    category: string,
    assetId: string
  ): ReturnType<typeof html> {
    // With no manager, or before its manifest has loaded, nothing can be drawn or
    // judged unknown yet: a placeholder icon, and no load, because a load before the
    // manifest rejects and its re-render would ask again on every repaint.
    if (!this.assetManager?.getManifest()) {
      return html`<sl-icon name="image"></sl-icon>`;
    }

    // A saved composition can reference an asset the manifest does not hold - the
    // drawing set is versioned independently of the compositions using it. getCached
    // answers such a reference with the question-mark glyph (rule 12, D-150), so the
    // tile below draws the question mark exactly as the drawing does, with no label,
    // and no load is attempted for it.
    const manager = this.assetManager;
    const failed = this.failedTileLoads.get(manager);
    const svg =
      manager.getCached({ category, name: assetId }) ??
      // A known glyph whose file failed to load draws the question mark, as the
      // drawing does for the same failure (ShapeRenderer.resolveAssets), and is not
      // asked for again by this manager.
      (failed?.has(`${category}/${assetId}`) ? UNKNOWN_ASSET_SVG : undefined);
    if (!svg) {
      // Trigger load for next render.
      void manager
        .loadAsset({ category, name: assetId })
        .then(() => {
          this.requestUpdate();
        })
        .catch(() => {
          // Fetch failed (file missing, offline). Remember it against this manager
          // so the next render settles on the question mark instead of fetching
          // again on every repaint; a new manager (a new manifest) asks afresh.
          const known = this.failedTileLoads.get(manager) ?? new Set<string>();
          known.add(`${category}/${assetId}`);
          this.failedTileLoads.set(manager, known);
          this.requestUpdate();
        });
      return html`<sl-spinner style="font-size: 1rem;"></sl-spinner>`;
    }

    // Process SVG to convert currentColor to explicit color.
    const assetColor = '#364f6b';
    let processedSvg = svg.replace(/currentColor/g, assetColor);

    // A glyph may define its own paint servers (the internal-track arrows carry
    // a `ptc-dots` pattern), and the tile, the canvas and every other tile all
    // live in this ONE shadow root, where url(#id) resolves to the first
    // definition in the tree. The canvas inlines the glyph with currentColor
    // intact and comes first, so without this every tile would paint with the
    // canvas's pattern instead of its own recoloured one. Suffixing each id
    // (and every url() that names one) with this tile's category and asset id
    // makes each tile reference its own definition. The canvas and viewers are
    // left alone: their copies of one glyph are identical, so whichever wins
    // draws the same thing. The same asset shown twice (picker tile or row, and the
    // Selected Light row) gets the same suffix, and those two copies are identical too.
    const scope = `${category}-${assetId}`;
    processedSvg = processedSvg
      .replace(/(\s)id="([^"]+)"/g, (_m, space: string, id: string) => {
        return `${space}id="${id}-${scope}"`;
      })
      .replace(/url\(#([^)]+)\)/g, (_m, id: string) => `url(#${id}-${scope})`);

    // Glyphs are borderless by design - they draw only an opening's interior
    // lines, because the frame model draws the opening itself. Alone in a tile
    // that reads as loose floating lines, so the preview composes the opening
    // and the drawing's labels back around it: the tile then matches how the
    // configuration appears in the customer's standard drawing set.
    //
    // The surround sits exactly on the glyph's own viewBox bounds, so the tile
    // also shows whether a glyph truly meets its edges. That viewBox differs
    // per asset (a French Door is twice as wide as a single leaf), so the tile
    // keeps each drawing's real aspect instead of forcing one shape.
    const entry = this.assetManager.getAssetEntry(category, assetId);
    const vb = parseViewBox(processedSvg) ?? { width: 100, height: 152 };
    const surround = `<rect x="0" y="0" width="${vb.width}" height="${vb.height}" fill="none"/>`;

    // Labels go through the same layout as the canvas (layoutLabelColumn:
    // authored positions, two-line columns stacked by line height), in the same
    // monospace face, so a tile places and draws them exactly as the drawing
    // does. The size is the one deliberate divergence: in glyph units (the
    // tile's viewBox is ~100x152), larger than the drawing's own label size
    // because a ~56px tile cannot show text at drawing scale.
    const PREVIEW_LABEL_FONT = 26;
    const label = layoutLabelColumn(
      entry?.labels ?? [],
      vb,
      { x: 0, y: 0, width: vb.width, height: vb.height },
      PREVIEW_LABEL_FONT
    )
      .map(
        (l) =>
          `<text x="${l.x.toFixed(1)}" y="${l.y.toFixed(1)}" text-anchor="${l.textAnchor}"` +
          ` font-family="${LABEL_FONT_FAMILY}" font-size="${l.fontSize.toFixed(1)}" font-weight="600"` +
          ` stroke="none" fill="${assetColor}">${l.text}</text>`
      )
      .join('');

    processedSvg = processedSvg
      .replace(
        /viewBox="[^"]*"/,
        `viewBox="-4 -4 ${vb.width + 8} ${vb.height + 8}"`
      )
      .replace(/(<svg[^>]*>)/, `$1${surround}${label}`);

    return html`${unsafeHTML(processedSvg)}`;
  }

  private renderAssetBrowser(
    currentAssets: Array<{ category: string; name: string }>
  ): ReturnType<typeof html> {
    const categories = this.assetManifest?.categories ?? {};
    const searchQuery = this.assetSearchQuery.toLowerCase().trim();

    const isSelected = (category: string, assetId: string) =>
      currentAssets.some((a) => a.category === category && a.name === assetId);

    // A search narrows by the full name, the family, the code, the tags, the id,
    // or the type's own name.
    const matches = (asset: AssetEntry, category: AssetCategory) =>
      !searchQuery ||
      asset.name.toLowerCase().includes(searchQuery) ||
      // Null-safe although typed as required: a newer bundle can briefly read an
      // older precached manifest with neither field (groupByFamily says why).
      ((asset.family as string | undefined)
        ?.toLowerCase()
        .includes(searchQuery) ??
        false) ||
      ((asset.code as string | undefined)
        ?.toLowerCase()
        .includes(searchQuery) ??
        false) ||
      (asset.tags ?? []).some((tag) =>
        tag.toLowerCase().includes(searchQuery)
      ) ||
      asset.id.toLowerCase().includes(searchQuery) ||
      category.name.toLowerCase().includes(searchQuery);

    const baseCategories: PickerCategory[] = [];
    const overlayCategories: PickerCategory[] = [];

    for (const [key, category] of Object.entries(categories)) {
      // Only offered entries are listed: a retired asset is hidden from the
      // browser but still resolves for drawings that already use it, which is
      // why the filter is offeredAssets and not a manifest edit.
      //
      // Families are grouped over everything offered and only then narrowed by
      // the search, so heading-or-row follows from how many drawings the family
      // HAS (rule 14), not from how many a search happens to leave on screen.
      const families = groupByFamily(offeredAssets(category))
        .map((group) => ({
          ...group,
          shown: group.assets.filter((asset) => matches(asset, category)),
        }))
        .filter((group) => group.shown.length > 0);
      if (families.length === 0) continue;

      const entry = { key, category, families };
      if (category.overlay) {
        overlayCategories.push(entry);
      } else {
        baseCategories.push(entry);
      }
    }

    return html`
      <div
        class="panel-header"
        style="display: flex; align-items: center; gap: 0.5rem;"
      >
        Asset Browser
      </div>
      <div
        style="padding: 0.5rem; border-bottom: 1px solid var(--sl-color-neutral-200);"
      >
        <!-- type="search": a search box is not a credential field, which is what keeps Safari and password managers from offering saved logins here (FORM-AUTOFILL; data-entry-autofill.md). -->
        <sl-input
          type="search"
          size="small"
          placeholder="Search assets..."
          clearable
          .value=${this.assetSearchQuery}
          @sl-input=${(e: Event) => {
            const input = e.target as HTMLInputElement;
            this.assetSearchQuery = input.value;
          }}
          @sl-clear=${() => {
            this.assetSearchQuery = '';
          }}
        >
          <sl-icon name="search" slot="prefix"></sl-icon>
        </sl-input>
      </div>
      <div class="asset-browser">
        ${baseCategories.map(({ key, category, families }) =>
          this.renderAssetCategory(key, category, families, isSelected)
        )}
        ${
          overlayCategories.length > 0
            ? html`
                <div
                  style="padding: 0.5rem 0.75rem; background: var(--sl-color-primary-100); font-size: var(--sl-font-size-x-small); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--sl-color-primary-700);"
                >
                  Overlay Assets
                </div>
                ${overlayCategories.map(({ key, category, families }) =>
                  this.renderAssetCategory(key, category, families, isSelected)
                )}
              `
            : nothing
        }
        ${
          baseCategories.length === 0 && overlayCategories.length === 0
            ? html`<div class="no-assets-message">
                No assets match "${this.assetSearchQuery}"
              </div>`
            : nothing
        }
      </div>
    `;
  }

  /**
   * One type's section of the picker (D-152, rule 14): under its collapsible
   * heading, in manifest order, a family of several drawings is a heading over
   * tiles labelled with the code alone, and a family of one is a single row with
   * its full name. Which one a family gets is its size, computed here and never
   * configured. Every tile and row carries the full name as its title and applies
   * the drawing on click.
   */
  private renderAssetCategory(
    key: string,
    category: AssetCategory,
    families: Array<AssetFamily & { shown: AssetEntry[] }>,
    isSelected: (category: string, assetId: string) => boolean
  ): ReturnType<typeof html> {
    return html`
      <div class="asset-category-section">
        <sl-details summary="${category.name}" open>
          <div class="asset-family-list">
            ${families.map((group) =>
              group.assets.length > 1
                ? html`
                    <div class="asset-family">
                      <div class="asset-family-title">${group.family}</div>
                      <div class="asset-category-grid">
                        ${group.shown.map(
                          (asset) => html`
                            <div
                              class="asset-grid-item asset-code-tile ${
                                isSelected(key, asset.id) ? 'selected' : ''
                              }"
                              @click=${() => this.handleAssetSelect(key, asset.id)}
                              title="${asset.name}"
                            >
                              <div class="asset-preview">
                                ${this.renderAssetThumbnail(key, asset.id)}
                              </div>
                              <div class="asset-label">${asset.code}</div>
                            </div>
                          `
                        )}
                      </div>
                    </div>
                  `
                : this.renderAssetRow(key, group.shown[0].id, {
                    pick: { selected: isSelected(key, group.shown[0].id) },
                  })
            )}
          </div>
        </sl-details>
      </div>
    `;
  }

  render() {
    return html`
      <div class="editor-shell" tabindex="0">
        <div class="toolbar">
          <div class="toolbar-group">
            <sl-tooltip content="New Composition">
              <sl-icon-button
                name="plus-circle"
                @click=${() => {
                  if (this.defaultSize) {
                    this.newDialogWidth = this.defaultSize.width;
                    this.newDialogHeight = this.defaultSize.height;
                  }
                  this.newDialogOpen = true;
                }}
              ></sl-icon-button>
            </sl-tooltip>
          </div>

          <div class="toolbar-divider"></div>

          <div class="toolbar-group">
            <sl-tooltip content="Undo (Ctrl+Z)">
              <sl-icon-button
                name="arrow-counterclockwise"
                ?disabled=${!this.canUndo}
                @click=${() => this.undo()}
              ></sl-icon-button>
            </sl-tooltip>
            <sl-tooltip content="Redo (Ctrl+Shift+Z)">
              <sl-icon-button
                name="arrow-clockwise"
                ?disabled=${!this.canRedo}
                @click=${() => this.redo()}
              ></sl-icon-button>
            </sl-tooltip>
          </div>

          <div class="toolbar-divider"></div>

          <div class="toolbar-group">
            <sl-tooltip content="Zoom In">
              <sl-icon-button
                name="zoom-in"
                @click=${() => {
                  this.previewScale = Math.min(4, this.previewScale + 0.1);
                }}
              ></sl-icon-button>
            </sl-tooltip>
            <sl-tooltip content="Zoom Out">
              <sl-icon-button
                name="zoom-out"
                @click=${() => {
                  this.previewScale = Math.max(0.1, this.previewScale - 0.1);
                }}
              ></sl-icon-button>
            </sl-tooltip>
            <sl-tooltip content="Fit to screen">
              <sl-icon-button
                name="aspect-ratio"
                ?disabled=${!this.grid}
                @click=${() => this.fitToExtents()}
              ></sl-icon-button>
            </sl-tooltip>
            <span class="zoom-readout"
              >${Math.round(this.previewScale * 100)}%</span
            >
          </div>

          <div class="toolbar-divider"></div>

          <div class="toolbar-group">
            <sl-tooltip
              content=${
                this.showDimensions ? 'Hide dimensions' : 'Show dimensions'
              }
            >
              <sl-icon-button
                class="toolbar-toggle ${this.showDimensions ? 'active' : ''}"
                name="rulers"
                @click=${() => {
                  this.showDimensions = !this.showDimensions;
                }}
              ></sl-icon-button>
            </sl-tooltip>
          </div>

          <div class="toolbar-spacer"></div>

          <div class="toolbar-group">
            <sl-button
              size="small"
              variant="primary"
              ?disabled=${!this.grid}
              @click=${() => this.exportSvg()}
            >
              <sl-icon slot="prefix" name="download"></sl-icon>
              Export SVG
            </sl-button>
            <!-- Host controls that belong on this toolbar (the AS 2047 page's fullscreen
                 toggle); the zoom viewer offers the same slot so read and edit put it in
                 one place. -->
            <slot name="toolbar-end"></slot>
          </div>
        </div>

        <!-- Center: Preview -->
        <div class="preview-area">
          ${
            this.grid
              ? html`
                  <div class="preview-container">
                    <div class="preview-svg" @click=${this.handlePreviewClick}>
                      ${this.svgContent ? unsafeHTML(this.svgContent) : nothing}
                    </div>
                  </div>
                `
              : html`
                  <div class="empty-state">
                    <sl-icon name="window"></sl-icon>
                    <p>No composition loaded</p>
                  </div>
                `
          }
          ${
            this.loading
              ? html`<div class="loading-overlay">
                  <sl-spinner></sl-spinner>
                </div>`
              : nothing
          }
        </div>

        <!-- Right Panel: Properties -->
        <div class="properties-panel">
          <div class="panel-header">Properties</div>
          <div class="panel-body">${this.renderPropertiesPanel()}</div>
        </div>

        <!-- Far Right Panel: Assets -->
        <div class="assets-panel">${this.renderAssetsPanel()}</div>
      </div>

      <!-- New Composition Dialog -->
      <sl-dialog
        label="New Composition"
        ?open=${this.newDialogOpen}
        @sl-after-hide=${() => {
          this.newDialogOpen = false;
        }}
      >
        <div
          style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);"
        >
          <sl-input
            autofocus
            label="Height (mm)"
            type="number"
            min="100"
            max="10000"
            value=${this.newDialogHeight}
            @sl-input=${(e: Event) => {
              this.newDialogHeight =
                parseInt((e.target as HTMLInputElement).value, 10) || 1000;
            }}
          ></sl-input>
          <sl-input
            label="Width (mm)"
            type="number"
            min="100"
            max="10000"
            value=${this.newDialogWidth}
            @sl-input=${(e: Event) => {
              this.newDialogWidth =
                parseInt((e.target as HTMLInputElement).value, 10) || 1000;
            }}
          ></sl-input>
          <sl-input
            label="Light Code"
            type="text"
            inputmode="numeric"
            value=${this.newDialogLightcode}
            placeholder="e.g. 232 (blank = single light)"
            help-text="Each digit is a column; the digit value is the number of rows in that column"
            @sl-input=${(e: Event) => {
              this.newDialogLightcode = (
                e.target as HTMLInputElement
              ).value.replace(/[^0-9]/g, '');
            }}
          ></sl-input>
        </div>
        <sl-button
          slot="footer"
          variant="primary"
          @click=${() => this.applyNewComposition()}
        >
          Create
        </sl-button>
      </sl-dialog>
    `;
  }

  /** The current authored grid. */
  getGrid(): GridComposition | null {
    return this.grid;
  }

  clearSelection(): void {
    this.selectedPath = null;
    this.selectedPaths = new Set();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ptc-editor': ShapeEditor;
  }
}
